import { useMemo } from 'react';

import { useGetLatestOversightEffectivenessByControlIds } from '@/hooks/queries';
import {
  isMoreRecentByTestDate,
  toTimestampOrNull,
} from '@/utils/oversightUtils';

export type OversightEffectivenessLeaf = {
  DesignEffectiveness: number | null;
  PerformanceEffectiveness: number | null;
  OverallEffectiveness: number | null;
  CreatedAt: string | null;
};

export type OversightEffectivenessByControl = Map<
  string,
  OversightEffectivenessLeaf
>;

const EMPTY_MAP: OversightEffectivenessByControl = new Map();

/**
 * Fetches the latest oversight effectiveness ratings (design / performance / overall)
 * per control for the given control Ids, keyed by ControlId.
 *
 * "Latest" mirrors the control page exactly
 * (getLatestOversightAssessmentTestResultsByControlId): the oversight result
 * with the newest `TestDate` (tie-broken by `CreatedAtTimestamp`), both read
 * from the result row itself. There is no completion gate — any oversight
 * assessment result counts, so the effectiveness surfaces as soon as it is
 * assigned.
 *
 * Unlike the risk variant, control_test_oversight_result has a direct
 * ParentControlId column, so each leaf maps to exactly one control. No fan-out
 * gymnastics required.
 *
 * Data fetching (GraphQL / tRPC feature-flag switch) is delegated to the
 * `useGetLatestOversightEffectivenessByControlIds` bridge hook; this hook owns
 * the "latest per control" reduction the register consumes.
 *
 * Skips the query when oversight viewing is disabled or no controls are visible.
 */
export const useLatestOversightEffectivenessByControls = (params: {
  controlIds: string[];
  isEnabled: boolean;
}): {
  effectivenessByControl: OversightEffectivenessByControl;
  loading: boolean;
} => {
  const { controlIds, isEnabled } = params;
  const skip = !isEnabled || controlIds.length === 0;

  const { data, loading } = useGetLatestOversightEffectivenessByControlIds({
    queryArgs: { controlIds },
    shouldSkip: skip,
  });

  const effectivenessByControl =
    useMemo<OversightEffectivenessByControl>(() => {
      if (!data) {
        return EMPTY_MAP;
      }

      const latestByControl = new Map<
        string,
        { leaf: OversightEffectivenessLeaf; TestDate: string | null }
      >();

      for (const row of data.control_test_oversight_result) {
        const testDate = toTimestampOrNull(row.TestDate);
        const createdAt = toTimestampOrNull(row.CreatedAtTimestamp);
        const leaf: OversightEffectivenessLeaf = {
          DesignEffectiveness: row.DesignEffectiveness ?? null,
          PerformanceEffectiveness: row.PerformanceEffectiveness ?? null,
          OverallEffectiveness: row.OverallEffectiveness ?? null,
          CreatedAt: createdAt,
        };
        const existing = latestByControl.get(row.ParentControlId);
        if (
          !existing ||
          isMoreRecentByTestDate(
            {
              TestDate: existing.TestDate,
              CreatedAt: existing.leaf.CreatedAt,
            },
            { TestDate: testDate, CreatedAt: createdAt }
          )
        ) {
          latestByControl.set(row.ParentControlId, {
            leaf,
            TestDate: testDate,
          });
        }
      }

      const result: OversightEffectivenessByControl = new Map();
      for (const [id, entry] of latestByControl) {
        result.set(id, entry.leaf);
      }

      return result;
    }, [data]);

  return {
    effectivenessByControl,
    loading,
  };
};
